export type WorkflowMode = 'lite' | 'full'

export type WorkflowRunStatus =
  | 'queued'
  | 'running'
  | 'waiting_for_user'
  | 'awaiting_approval'
  | 'paused'
  | 'completed'
  | 'completed_with_unknowns'
  | 'blocked'
  | 'budget_limited'
  | 'guardrail_stopped'
  | 'failed'
  | 'cancelled'

/**
 * Human-readable labels for run/step statuses. The raw snake_case enum
 * ("completed_with_unknowns") reads as a bug to a plant engineer; the meaning is
 * preserved — only the display is humanized. Unmapped values de-underscore.
 * Typed against the status unions so a misspelled key fails the compiler.
 */
export const WORKFLOW_STATUS_LABELS: Partial<
  Record<WorkflowRunStatus | WorkflowStepStatus, string>
> = {
  queued: 'Queued',
  running: 'Running',
  waiting_for_user: 'Waiting for you',
  awaiting_approval: 'Awaiting approval',
  paused: 'Paused',
  completed: 'Completed',
  completed_with_unknowns: 'Completed · open questions',
  blocked: 'Blocked',
  budget_limited: 'Stopped · budget reached',
  guardrail_stopped: 'Stopped for safety',
  failed: 'Failed',
  cancelled: 'Cancelled',
  pending: 'Pending',
  skipped: 'Skipped',
}

export function workflowStatusLabel(status: string): string {
  return (
    WORKFLOW_STATUS_LABELS[status as keyof typeof WORKFLOW_STATUS_LABELS] ??
    status.replace(/_/g, ' ')
  )
}

export type WorkflowStepStatus =
  | 'pending'
  | 'running'
  | 'waiting_for_user'
  | 'completed'
  | 'blocked'
  | 'failed'
  | 'skipped'

/** A single step inside a WorkflowDefinition (loose subset the page reads). */
export interface WorkflowDefinitionStep {
  id: string
  title: string
  kind: string
  phase?: string
  allowedTools?: string[]
  outputKey?: string
  /** Decision steps: the value→target-step map (read/edited by the flowchart editor). */
  cases?: Record<string, string>
  /** Decision steps: the fallthrough target when no case matches. */
  default?: string
  /** Decision steps: the state key whose value selects a case. */
  stateKey?: string
}

export interface WorkflowDefinition {
  id: string
  version: string
  title: string
  description: string
  family?: string
  steps: WorkflowDefinitionStep[]
  /** Authoring clarify-pass Q&A persisted by the generator (#25721); copied
   *  onto the start-run request so REUSED templates carry session answers. */
  authoringClarifications?: Array<{ question: string; answer: string }>
  /** Carried through unchanged on the edit round-trip (the validator reads the full shape). */
  [key: string]: unknown
}

export interface WorkflowPlanPreview {
  title: string
  operators: string[]
  mode: WorkflowMode
  /** Declared workflow family — drives neutral (non-troubleshooting) result framing for a
   *  `general` workflow (e.g. a HAZOP node definition). Absent on older cached runs. */
  family?: 'troubleshooting' | 'general'
  phases: Array<{
    id: string
    title: string
    phase?: string
    mayAskClarification: boolean
    likelyTools: string[]
    completionCriteria: string
  }>
  budgets: {
    maxDurationMs: number
    maxAgentStepsPerWorkflowStep: number
    maxToolCalls?: number
  }
  goal?: unknown
}

/**
 * A v2 plan preview, recognised by what it CANNOT carry: the v2 projection has no operator
 * personas and no budgets (`plan-preview.ts` documents both omissions), because a v2 run is not
 * executed by the v1 runner that enforces them. Used to decide whether the HITL checkbox is a
 * control or a report, and which planning-summary wording to show.
 */
export function isV2Plan(planPreview: WorkflowPlanPreview | null | undefined): boolean {
  if (!planPreview) return false
  return (planPreview.operators?.length ?? 0) === 0 && planPreview.budgets === undefined
}

export interface WorkflowValidationResult {
  ok: boolean
  issues?: Array<{ path: string; message: string }>
}

export interface GenerateWorkflowDraftRequest {
  mode: 'workflow_chat' | 'configuration'
  prompt?: string
  /** A registered template id to preview/run (base, verified, 5whys, ...). */
  presetHint?: string
  /** Recipe source 2: raw text of a process document to extract a workflow from. */
  documentText?: string
}

/** An AI agent that runs part of a workflow (one per agentic step). */
export interface WorkflowTemplateExecutor {
  id: string
  name: string
}

/** Summary row returned by GET .../templates (mirrors backend WorkflowSummary). */
export interface WorkflowTemplateSummary {
  id: string
  version: string
  title: string
  description: string
  family?: 'troubleshooting' | 'general'
  stepCount: number
  /** Code-resident built-in (read-only) vs tenant-authored store row. */
  builtin: boolean
  /** AI agents that run this workflow, derived from its agentic steps. */
  executors: WorkflowTemplateExecutor[]
  /** ISO timestamp the template row was created; null for built-ins. */
  createdAt: string | null
  /** Author of the template row; null for built-ins (FS item 7). */
  createdBy?: string | null
  /** ISO timestamp this workflow was last run; null if never run. */
  lastOpenedAt: string | null
  /** Engineering discipline; null until set. */
  discipline: string | null
}

export interface GenerateWorkflowDraftResponse {
  definition: WorkflowDefinition
  planPreview: WorkflowPlanPreview
  /**
   * OPTIONAL, matching the backend contract it mirrors.
   *
   * Declared required here while `GenerateWorkflowDraftResponse` on the server has it
   * optional, so `draft.validation.ok` type-checked and then threw at runtime the first time a
   * producer left it out — the registry-backed canvas (#28932) — and React rendered the 500
   * page. The type was the defect: a lie in a shared shape defeats every consumer's compiler.
   */
  validation?: WorkflowValidationResult
  sourceTemplateId: string
  /**
   * Set when the canvas is the v2 engine's plan (matched blueprint, unmatched DAG, or
   * sequenced template fallback). The hook must not persist that carrier as an authored
   * v1 template. Unmatched compiled steps ride `compiledSteps` onto start.
   */
  drawnBy?: 'v2'
  /**
   * Compiled v2 cards (toolPlans intact). Echoed on start-run. The server trusts the body after
   * a kind check — it does not verify this is the plan it drafted. Not the `definition` — that
   * carrier strips `toolPlan` into agentic stubs.
   *
   * WHETHER IT RUNS IS THE SERVER'S CALL, not this field's. With blueprint routing ON it feeds
   * `drawnPlan`; with routing OFF the engine authors from the question and this is not read
   * (#31774). The canvas stays truthful either way — `createRun` projects it from whatever runs
   * (#28786) — so what this carries is the drawn cards, not a guarantee they execute.
   */
  requestedPlan?: RequestedV2Plan
  /** Whether generate-draft matched a published blueprint. */
  matched?: boolean
  /**
   * Unmatched `previewPlanFor` steps — the preview's own output echoed back, never something the
   * reader edited. Absent on a catalog match. Start forwards these as `authoredPlan`. Same echo
   * as `requestedPlan`: the server does not verify these are the steps it drafted.
   *
   * IT NO LONGER SUPPRESSES THE PLANNER when blueprint routing is off. It used to — and that was
   * the bug: a preview that succeeded disabled planning, so the run skipped the scope gate and
   * asked the reader for nothing, five cards covering no requirement (#31774). The only reason it
   * had not bitten earlier is that previews used to draw a lone placeholder, which the server
   * classified as not-a-DAG. With routing ON this still feeds the run.
   */
  compiledSteps?: Array<{
    id: string
    title: string
    kind: string
    instruction?: string
    toolPlan?: Array<{ tool: string; args: Record<string, unknown> }>
  }>
}

/** One option of a generation-time clarifying question (Figma chat flow). */
export interface WorkflowClarifyingOption {
  label: string
  description?: string
}

/** Cursor-style ask the generator can return instead of a draft. */
export interface WorkflowClarifyingQuestion {
  question: string
  options: WorkflowClarifyingOption[]
  allowCustom: boolean
}

/** Section-9 template ask: a registered template the request clearly matches. */
export interface WorkflowTemplateSuggestion {
  templateId: string
  title: string
  description: string
  /** builtin = shipped template (preview it); tenant = the user's OWN workflow (reopen it). */
  source?: 'builtin' | 'tenant'
}

/**
 * drafts/generate result: a draft, OR clarifying questions to answer first
 * (the client re-calls with clarificationAnswers), optionally with a template
 * the triage recognized.
 */
export type GenerateWorkflowDraftResult =
  | GenerateWorkflowDraftResponse
  | {
      clarifyingQuestions: WorkflowClarifyingQuestion[]
      templateSuggestion?: WorkflowTemplateSuggestion
    }

export interface WorkflowKgReadinessResult {
  ready: boolean
  reason?: string
  warnings: string[]
  historianIntent: boolean
  availableTimeseriesDocuments: unknown[]
  availableHistorianTags: unknown[]
  suggestedHistorianTags: unknown[]
  explicitHistorianTags: string[]
  matchedHistorianTags: unknown[]
  missingHistorianTags: string[]
  processedSurfaces: Record<string, number>
}

/**
 * Compiled v2 cards the reader approved. Start-run echoes this as `requestedPlan`.
 *
 * The echo is the contract, not a receipt: the server runs the posted steps
 * after a kind check (`isV1ShapedPlan`). It does not hash or cache the draft.
 * Same authority as chat; tools still pass the granular allow-list + schema.
 */
export interface RequestedV2Plan {
  steps: Array<{
    id: string
    title: string
    kind: string
    allowedTools?: string[]
    toolPlan?: Array<{ tool: string; args: Record<string, unknown> }>
  }>
  name?: string
}

export interface StartWorkflowRunRequest {
  /** A registered template id: built-ins (base / verified / 5whys) or a published tenant template. */
  templateId: string
  /**
   * Echo of generate-draft's `requestedPlan`. The server does not verify this
   * is the plan it drafted — see `RequestedV2Plan`. Never the `definition`.
   */
  requestedPlan?: RequestedV2Plan
  input: {
    problem: string
    timeWindow?: { start?: string; end?: string }
    scope?: {
      workspaceId?: string
      tags?: string[]
      equipment?: string[]
      documentIds?: string[]
      boardId?: string
    }
    mode: WorkflowMode
    kgReadiness?: WorkflowKgReadinessResult
    /** Session authoring answers for the runner's ledger seed + per-run persona
     *  (a reused template's stored definition cannot carry them). */
    authoringClarifications?: Array<{ question: string; answer: string }>
    /**
     * "Evidence verification" toggle: when true on a troubleshooting_base run,
     * the backend appends an adversarial verify-diagnosis gate to the spine.
     * Ignored on templates that own their own verify (5-Whys, HAZOP, …).
     */
    verification?: boolean
  }
  /**
   * Unmatched v2 preview steps. Start forwards these so the run executes the
   * canvas generate-draft already drew. Omit on a catalog match.
   */
  compiledSteps?: GenerateWorkflowDraftResponse['compiledSteps']
  idempotencyKey?: string
}

export interface StartWorkflowRunResponse {
  workflowRunId: string
  templateId: string
  templateVersion: string
  status: WorkflowRunStatus
  planPreview: WorkflowPlanPreview
}

/**
 * A METHOD supplied whole — the procedure document (or interview transcript) that answers the
 * intake's three questions in one move.
 *
 * The backend has accepted this since the intake shipped (`validateV2MethodSource`) and nothing has
 * ever sent it: the "Send a document" button posted its own label, which the backend discards as a
 * choice rather than an answer, so the run re-parked on the same question. `text` is the extracted
 * document, not a file — extraction happens before this call.
 */
export interface SuppliedMethodSource {
  kind: 'document' | 'interview'
  text: string
  title?: string
  /**
   * Catalog id of what the method came from, so the run's provenance points at a real
   * artifact rather than just a filename. Accepted and persisted by the backend
   * (`v2-run.service.ts` validates it and passes it through); omitted for a local file,
   * which has no catalog row to point at.
   */
  ref?: string
}

export interface ResumeWorkflowRunRequest {
  clarificationAnswers?: Record<string, string>
  methodSource?: SuppliedMethodSource
  budgetExtension?: {
    maxDurationMs?: number
    maxToolCalls?: number
  }
}

/** What `POST /v1/document/extract-text` returns — parsed text, and why it may disappoint. */
export interface ExtractedDocumentText {
  text: string
  file_type: string
  page_count: number
  truncated: boolean
}

export interface WorkflowRunView {
  id: string
  conversationId: string
  templateId: string
  templateVersion: string
  status: WorkflowRunStatus
  mode: WorkflowMode
  input: Record<string, unknown>
  planPreview: WorkflowPlanPreview
  finalOutput: Record<string, unknown> | null
  state: Record<string, unknown>
  progress: Record<string, unknown>
  /** Run-level failure detail — why a run died outside any step. Optional: pre-existing cached runs omit it. */
  error?: Record<string, unknown> | null
  startedAt: string | null
  completedAt: string | null
  cancelledAt: string | null
  createdAt?: string
}

export interface WorkflowStepRunView {
  id: string
  workflowRunId: string
  stepId: string
  stepIndex: number
  phase: string | null
  title: string
  kind: string
  status: WorkflowStepStatus
  input: Record<string, unknown> | null
  output: Record<string, unknown> | null
  evidence: unknown[] | null
  toolCalls: unknown[] | null
  /** Per-step Langfuse trace id → the `/kg-trace/<id>` deep link (same forensics
   *  page the chat magnifying glass opens). Null/absent for step kinds with no
   *  agent sub-run (decision/clarification/output) and for pre-existing runs. */
  correlationId?: string | null
  startedAt: string | null
  completedAt: string | null
  error: Record<string, unknown> | null
}

/** Read-only control-flow graph projected from a validated definition (see workflowApi.getWorkflowTemplateGraph). */
export type WorkflowGraphEdgeKind = 'next' | 'branch' | 'fanout' | 'retry'

export interface WorkflowControlFlowNode {
  id: string
  kind: string
  title: string
  phase: string | null
  terminal: boolean
  parentId?: string
}

export interface WorkflowControlFlowEdge {
  from: string
  to: string
  kind: WorkflowGraphEdgeKind
  label?: string
}

export interface WorkflowControlFlowGraph {
  templateId: string
  version: string
  nodes: WorkflowControlFlowNode[]
  edges: WorkflowControlFlowEdge[]
}

/** Lightweight live progress frame from the run SSE stream (see workflowApi.subscribeWorkflowRunProgress). */
export interface WorkflowProgressStepFrame {
  stepId: string
  stepIndex: number
  title: string
  phase: string | null
  kind: string
  status: string
}

export interface WorkflowProgressFrame {
  runId: string
  status: WorkflowRunStatus
  terminal: boolean
  parked: boolean
  steps: WorkflowProgressStepFrame[]
  completedSteps: number
  totalSteps: number
  progress: Record<string, unknown>
  updatedAt: string
  /**
   * What the run's triage RESOLVED — the sources it is about to reason over. Emitted
   * by the backend on the frame the client already polls (Khang, 2026-08-03), so
   * surfacing it costs no new endpoint. Absent on a run with no readiness preflight.
   */
  resolvedSources?: WorkflowResolvedSources
}

/** The drawings and tags a run resolved before reasoning — rendered as the sources gate. */
export interface WorkflowResolvedSources {
  pnids: Array<{ documentId?: string; documentName?: string }>
  historianTags: Array<{ tagName: string; tagId?: string; requested?: string }>
  /** Tags the user named that the catalog could NOT resolve — the honest half. */
  unmatchedHistorianTags: string[]
}

export interface NormalizedToolCall {
  id: string
  name: string
  status: 'ok' | 'error' | 'running'
  input?: unknown
  output?: unknown
  error?: unknown
  raw: unknown
  step: WorkflowStepRunView
}

export type WorkflowGraphNodeKind = 'step' | 'tool' | 'step_output'

export type WorkflowPlanPhase = WorkflowPlanPreview['phases'][number]

export interface WorkflowGraphNodeData {
  kind: WorkflowGraphNodeKind
  label: string
  status: string
  title: string
  subtitle?: string
  /**
   * EFFECTIVE HITL for this step (plan default overridden by the persisted
   * hitlByStep row — resolved in the builder). The card renders it as a
   * display-only checked row; the toggle lives in the node-settings panel
   * (Putra "10. Update": cannot uncheck on the card).
   */
  hitl?: boolean
  hitlReadonly?: boolean
  isAgent?: boolean
  isVerify?: boolean
  clickable?: boolean
  step?: WorkflowStepRunView
  toolCall?: NormalizedToolCall
  /** Tool calls a step made, rendered inline as friendly chips on the step node. */
  toolCalls?: NormalizedToolCall[]
  /** Click-through for an inline tool chip → the tool-call detail dialog. */
  onSelectTool?: (toolCall: NormalizedToolCall) => void
  /** When set (first node only), renders the Run Workflow pill attached above the card's head. */
  onRun?: () => void
  /** First node only: the run is in flight → the head pill shows a "Running…"
   *  loading state (spinner) instead of the clickable Run Workflow button. */
  running?: boolean
  /** First node only, run in flight: the head pill becomes an actionable
   *  "Stop Workflow" (Figma "11. Stop") instead of the passive "Running…". */
  onStop?: () => void
  /** The stop request is in flight → the Stop pill shows a "Stopping…" spinner. */
  stopping?: boolean
  draftPhase?: WorkflowPlanPhase
}

/**
 * Did this run execute on the workflow-v2 engine?
 *
 * `RunRecordService` stamps `state.engine = 'v2'` on every v2 run, and `toRunView` already
 * serializes `state`, so no backend change is needed to read it.
 *
 * A guarded read rather than a typed field on purpose: `state` is a jsonb blob shared with
 * v1, and every historical run predates the key — so `undefined` must mean v1, not a
 * parse failure.
 */
export function isV2Run(run: Pick<WorkflowRunView, 'state'>): boolean {
  return run.state?.engine === 'v2'
}

/**
 * The step id the engine queues BEFORE a question plan exists.
 *
 * `QUESTION_PLAN_PLACEHOLDER_ID` in
 * `backend/web/src/kg-agent/workflows-v2/question-plan-placeholder.ts`. A run parked on it has a
 * canvas card ("Planning from the question") and no plan, which is the shape the guard below
 * cannot tell apart from a real plan by counting steps.
 *
 * THE PATH IS IN THIS COMMENT ON PURPOSE: it is the only thing linking the two, so a rename on the
 * backend has to be found by whoever greps for it. The failure direction is the mild one — the
 * frontend stops hiding the button and the 409 comes back — but it is silent.
 */
const AUTHOR_PLAN_STEP_ID = 'author-plan'

/**
 * The step kinds the engine can actually re-run — `KINDS` in
 * `backend/web/src/kg-agent/workflows-v2/blueprints/blueprint-compiler.ts`.
 *
 * MIRRORED BECAUSE THE BACKEND FILTERS ON IT. `storedSteps` (`v2-run.service.ts`) drops every
 * stored step whose `kind` is not in that set before deciding whether there is a plan, so a run
 * whose stored list holds only legacy or malformed kinds reads as "no plan to re-run" there and
 * 409s — while a guard here that accepts any string kind shows the button (greptile P2). Testing
 * `typeof kind === 'string'` was answering a different question than the backend asks.
 *
 * THE TWO DIRECTIONS OF DRIFT ARE NOT THE SAME, and only one of them is safe.
 *
 * A kind ADDED to the backend and not here makes the button disappear until this list catches up.
 * That is the safe half: the reader gets "Run again", which starts the question afresh, instead of
 * a button that fails.
 *
 * A kind REMOVED from the backend and left here is the OTHER half, and it fails the way this guard
 * exists to prevent. `storedSteps` filters on the backend's `KINDS`, so the moment a kind leaves
 * that set the engine drops those steps from a stored plan — while this list still counts them, so
 * the button is shown, and `retryRun` then refuses with the very 409 the guard was added to stop.
 * The reader clicks and nothing explains it.
 *
 * NOTHING CATCHES THAT. There is no shared constant, no generated type and no test spanning the two
 * — the mirror is this comment and the path above it. So a retirement is the case to be careful
 * about: remove the kind HERE FIRST (safe half, button hides), then from the backend. That ordering
 * is the whole mitigation, and it is why the durable fix is the backend answering the question
 * instead — see the `canRetry` follow-up linked on {@link hasStoredPlan}.
 */
const RERUNNABLE_STEP_KINDS = new Set([
  'gather',
  'reason',
  'calc',
  'review',
  'decision',
  'hitl',
  'resolve',
  'output',
])

/**
 * Is there a stored plan for `retryRun` to re-run?
 *
 * MIRRORS THE BACKEND GUARD — the part of it a client can decide. `V2RunService.retryRun` REFUSES
 * in eight places, and only three of them read facts the run view carries:
 *
 *     if (!RETRYABLE_RUN_STATUSES.includes(run.status))     -> "run is X, and only … can be retried"
 *     if (!question || !steps || steps.length === 0)        -> "the run carries no question"
 *                                                          -> "the run carries no plan to re-run"
 *     if (isPlaceholderOnly(steps))                         -> "the run never got a plan — start
 *                                                              the question again rather than
 *                                                              retrying"
 *
 * That is FOUR shapes across THREE blocks, not three shapes in one, which is what this docblock
 * said until a gap check opened the method and counted (in review on this PR). The other five
 * refusals are server facts no client can mirror: the runtime being unavailable, the run not
 * existing FOR THIS TENANT, the run belonging to someone else, and the two drain checks either
 * side of the claim.
 *
 * REFUSES, NOT THROWS — the words count different things and the first draft said the second.
 * `retryRun` holds NINE `throw` statements; the ninth is `throw err` in the catch around the
 * hand-off, which re-raises somebody else's failure after deleting the `liveRuns` entry rather
 * than refusing anything of its own. Excluding it from the inventory is right, so the count was
 * right and only the noun was wrong — which matters because the next person to check this will
 * grep `throw` and get nine (review).
 *
 * THE QUESTION CLAUSE IS THE ONE THIS MIRROR MISSED, and it is the first condition of the middle
 * block. `retryRun` re-runs the stored plan AGAINST the stored question, so a row with steps and no
 * question refuses exactly as one with a question and no steps does — and the button was still
 * offered for it, which is the 409 this function exists to stop, reached through the clause it did
 * not copy (gap check on #32029, after it merged).
 *
 * READ OFF `input.steps`, which the run view already carries verbatim — `toRunView` passes
 * `run.input` through — so this needs nothing new from the API. `planPreview.phases` is the other
 * candidate and it is worse: a placeholder run has exactly one phase, so the count that would
 * separate them is the same count, and `planPreview` can be present-but-empty on a real plan.
 *
 * ── TEMPORARY, AND TRACKED AS SUCH ───────────────────────────────────────────
 *
 * This function is a MIRROR of a decision the backend already makes, and it is the second place
 * those refusals are now written. Two constants here copy backend state with nothing but a comment
 * linking them — {@link AUTHOR_PLAN_STEP_ID} and {@link RERUNNABLE_STEP_KINDS} — and the removal
 * direction of that coupling silently reintroduces the 409 this guard was added to stop.
 *
 * AND THE MIRROR IS ONE WIDER THAN THIS FILE. The status shape above — the first of the four — has
 * no counterpart here. It is a hardcoded array in `builder/TroubleshootingPanels.tsx`, which
 * decides whether `retryFailedRunCopy` is called at all:
 *
 *     workflow.run && ['failed', 'blocked', 'budget_limited'].includes(workflow.run.status)
 *
 * Checked at the time of writing and it MATCHES exactly — `RETRYABLE_RUN_STATUSES` is those same
 * three, and the four-status array further down that file (`guardrail_stopped` included) gates the
 * run-error block rather than the button, so its extra member is correct there and does not leak
 * into retry. No bug today.
 *
 * It is recorded because the drift surface is larger than an inventory written only in this file,
 * and a reader auditing the retry mirror will not find that array by reading this one. It has the
 * same asymmetry as the constants above: a status REMOVED from `RETRYABLE_RUN_STATUSES` and left
 * in the panel brings the 409 back silently. `canRetry` deletes it along with the rest.
 *
 * The durable fix is the backend answering the question: a `canRetry` on the run view, decided
 * beside the `retryRun` guard that enforces it, which deletes both constants and lets the REASON
 * reach the reader instead of a button that fails.
 *
 * **#32033** carries that, so this is legible as temporary to someone other than its author, and
 * so the mitigation for a kind retirement (drop it HERE first, then from the backend) is written
 * somewhere other than a docblock nobody greps.
 */
export function hasStoredPlan(run: Pick<WorkflowRunView, 'input'>): boolean {
  // THE SAME TRIM THE BACKEND APPLIES. `retryRun` reads `typeof stored.question === 'string' ?
  // stored.question.trim() : ''` and refuses on the empty string, so a whitespace-only question is
  // a refusal there and must be one here.
  const question = (run.input as { question?: unknown }).question
  if (typeof question !== 'string' || question.trim() === '') return false
  const steps = (run.input as { steps?: unknown }).steps
  if (!Array.isArray(steps)) return false
  const real = steps.filter(
    (step): step is { id: string; kind: string } =>
      typeof step === 'object' &&
      step !== null &&
      typeof (step as { id?: unknown }).id === 'string' &&
      typeof (step as { kind?: unknown }).kind === 'string' &&
      // THE BACKEND'S OWN ALLOWLIST, not merely "a string" — see `RERUNNABLE_STEP_KINDS`. A stored
      // list of kinds the compiler does not know is filtered to nothing there, so the retry it
      // offers is one the backend refuses.
      RERUNNABLE_STEP_KINDS.has((step as { kind: string }).kind),
  )
  if (real.length === 0) return false
  return !(real.length === 1 && real[0]?.id === AUTHOR_PLAN_STEP_ID)
}

/**
 * Copy for the failed-run retry control, or `null` when there is nothing to retry.
 *
 * v1 really does resume from the failed step and keep completed evidence. v2
 * re-runs the stored plan from the start (`V2RunService.retryRun`), so the
 * v1 wording was a lie on every stamped v2 row.
 *
 * AND A PLAN IS WHAT v2 RE-RUNS, so a run that never got one has no retry. The gate was status
 * alone, which offered "Retry from the start — re-runs the stored plan" on a run whose planner
 * timed out; clicking it 409s with "the run carries no plan to re-run". Dev `503bcd53` and
 * `00602d49` (2026-09-11) both ended that way. The panel already shows the right control beside
 * it — "Run again", which starts the question afresh, which is what the backend's own refusal
 * tells the reader to do.
 */
export function retryFailedRunCopy(run: Pick<WorkflowRunView, 'state' | 'input'>): {
  button: string
  hint: string
} | null {
  if (isV2Run(run)) {
    if (!hasStoredPlan(run)) return null
    return { button: 'Retry from the start', hint: 'Re-runs the stored plan.' }
  }
  return { button: 'Retry from failed step', hint: 'Evidence from completed steps is kept.' }
}
